/**
 * Saha İzleme — ZIYARET_TAKIP.md §5.3. KATMAN 3 (hareket logu).
 *
 * Eski pr-go "Saha İzleme" ekranının karşılığı ama akış DÜZ LİSTE DEĞİL, **müşteri bazında
 * gruplanmış**: her grup bir ziyaret (Katman 2), grubun içi o ziyaretin hareketleri (Katman 3).
 * Sağda video düzenindeki canlı özet kartları korunur.
 *
 * Sunucu düz ve sıralı hareket listesi döner; gruplama BURADA yapılır çünkü grup sınırı
 * `visitId` değişimidir ve ziyaret dışı hareketler (gün açılış/kapanış) kendi başına satırdır.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ACTIVITY_GROUPS, activityTarget, activityTargetLabel,
  fmtDuration, fmtMinutes, fmtMoney, fmtNum, fmtPct, fmtTime,
  OUTCOME_LABELS, OUTCOME_PILL, PAYMENT_LABELS,
} from './shared';
import { IcChevron, IcFoldDown, IcFoldUp, IcRefresh } from './icons';

export function FieldWatchTab({
  activities, dayDetail, loading, autoRefresh, onToggleAutoRefresh, refreshSec,
  onOpenDocument, contextLabel,
}) {
  const [group, setGroup] = useState('');
  const [includeGps, setIncludeGps] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const items = useMemo(() => {
    let list = activities || [];
    if (group) list = list.filter((a) => a.activityGroup === group);
    if (!includeGps) list = list.filter((a) => a.activityGroup !== 'gps');
    return list;
  }, [activities, group, includeGps]);

  /** Ardışık aynı visitId'li hareketleri tek gruba toplar; visitId yoksa tek satırlık gün grubu. */
  const groups = useMemo(() => buildGroups(items), [items]);

  // Filtre değişince yeni gruplar açık başlasın — kullanıcı daralttıklarını kaybetmesin diye
  // yalnız artık var olmayan grup anahtarları temizlenir.
  useEffect(() => {
    setCollapsed((prev) => {
      const keys = new Set(groups.map((g) => g.key));
      const next = new Set([...prev].filter((k) => keys.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [groups]);

  const visitGroups = groups.filter((g) => g.visitId);
  const anyOpen = visitGroups.some((g) => !collapsed.has(g.key));

  /** §5.3 "Tümünü Daralt / Genişlet" — gün açılış/kapanış satırları bundan ETKİLENMEZ. */
  function toggleAll() {
    setCollapsed(anyOpen ? new Set(visitGroups.map((g) => g.key)) : new Set());
  }

  return (
    <div className="pane on">
      {contextLabel && (
        <div className="alert info m" style={{ marginBottom: 12 }}><div>{contextLabel}</div></div>
      )}

      <div className="tlgrid">
        <div className="card">
          <div className="sec-h" style={{ padding: '14px 16px 0', marginBottom: 10 }}>
            <div>
              <b>Hareket Akışı</b>
              <span className="sub" style={{ display: 'block' }}>
                Müşteri bazında gruplanmış · her satır bir hareket kaydı
              </span>
            </div>
            <div style={{ flex: 1 }} />
            <button type="button" className={`chip ${autoRefresh ? 'on' : ''}`} onClick={onToggleAutoRefresh}>
              <IcRefresh size={13} />
              {autoRefresh ? `${refreshSec} sn'de bir yenilenir` : 'Otomatik yenileme kapalı'}
            </button>
          </div>

          <div className="quickbar">
            {ACTIVITY_GROUPS.map((g) => (
              <span key={g.key} className={`chip ${group === g.key ? 'on' : ''}`}
                role="button" tabIndex={0}
                onClick={() => setGroup(g.key)}
                onKeyDown={(e) => e.key === 'Enter' && setGroup(g.key)}>
                {g.label}
              </span>
            ))}
            {/* GPS varsayılan KAPALI — dakikalık ping akışı boğar (§5.3). */}
            <span className={`chip ${includeGps ? 'on' : ''}`} role="button" tabIndex={0}
              onClick={() => setIncludeGps((v) => !v)}
              onKeyDown={(e) => e.key === 'Enter' && setIncludeGps((v) => !v)}>
              GPS {includeGps ? '(açık)' : '(gizli)'}
            </span>

            <button type="button" className="chip tgl" style={{ marginLeft: 'auto' }}
              onClick={toggleAll} disabled={visitGroups.length === 0}>
              {anyOpen ? <IcFoldUp size={13} /> : <IcFoldDown size={13} />}
              <span>{anyOpen ? 'Tümünü Daralt' : 'Tümünü Genişlet'}</span>
            </button>
            <span style={{ color: 'var(--t3)', fontSize: 11.5, fontWeight: 600 }}>
              {items.length} hareket · {visitGroups.length} ziyaret
            </span>
          </div>

          {loading && <div style={{ padding: 24, textAlign: 'center' }}>Yükleniyor…</div>}

          {!loading && groups.length === 0 && (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--t3)', fontSize: 12.5 }}>
              Bu gün için hareket kaydı yok.
              <div style={{ marginTop: 6, fontSize: 11.5 }}>
                Hareket logu mobil uygulamanın merkezî logger'ı yayına girdiği tarihten
                itibaren dolar; öncesi için akış boştur.
              </div>
            </div>
          )}

          {!loading && groups.map((g) => (
            <ActivityGroup key={g.key} group={g}
              open={!collapsed.has(g.key)}
              onToggle={() => setCollapsed((prev) => {
                const next = new Set(prev);
                if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
                return next;
              })}
              onOpenDocument={onOpenDocument} />
          ))}
        </div>

        {/* ── Sağ sütun: canlı özet kartları (video düzeni korunur) ──── */}
        <div className="tlside">
          {/* #777.6 Oturum seçilmemişken kartlar filtredeki TÜM oturumların toplamıdır — sayının
              neyin toplamı olduğu belirsiz kalmasın. */}
          {dayDetail?.sessionCount > 1 && (
            <div className="alert info m" style={{ marginBottom: 10, fontSize: 11.5 }}>
              <div>{fmtNum(dayDetail.sessionCount)} oturumun toplamı. Tek temsilciye inmek için Kokpit'ten satır seçin.</div>
            </div>
          )}
          <SummaryCard title="Sipariş" badge={`${fmtNum(dayDetail?.orderCount)} adet`}
            rows={[
              ['Toplam', fmtMoney(dayDetail?.orderAmount)],
              ['Çeşit', fmtNum(dayDetail?.orderSkuCount)],
            ]} />
          <SummaryCard title="Fatura" badge={`${fmtNum(dayDetail?.invoiceCount)} adet`}
            rows={[
              ['Toplam', fmtMoney(dayDetail?.invoiceAmount)],
              ['Çeşit', fmtNum(dayDetail?.invoiceSkuCount)],
            ]} />
          <SummaryCard title="İrsaliye" badge={`${fmtNum(dayDetail?.dispatchCount)} adet`}
            rows={[['Toplam', fmtMoney(dayDetail?.dispatchAmount)]]} />
          <SummaryCard title="Tahsilat" badge={fmtMoney(dayDetail?.collectionAmount)}
            rows={[
              ['Nakit', fmtMoney(dayDetail?.collectionCash)],
              ['Çek', fmtMoney(dayDetail?.collectionCheque)],
              ['Senet', fmtMoney(dayDetail?.collectionNote)],
              ['Kredi Kartı', fmtMoney(dayDetail?.collectionCard)],
              ['DBS', fmtMoney(dayDetail?.collectionDbs)],
            ]} />
          <SummaryCard title="Ziyaret Analizi"
            rows={[
              ['Rut Nokta', fmtNum(dayDetail?.plannedPointCount)],
              ['Gidilen Nokta', fmtNum(dayDetail?.visitedPointCount)],
              ['Ziyaret Sayısı', fmtNum(dayDetail?.visitCount)],
              ['Başarılı', fmtNum(dayDetail?.successVisitCount)],
              ['Başarısız', fmtNum(dayDetail?.failVisitCount)],
              ['Gerçekleşen %', fmtPct(dayDetail?.realizationPct)],
            ]} />
          <SummaryCard title="Rut Analizi"
            rows={[
              ['Güne Başlama', fmtTime(dayDetail?.startedAt)],
              // #788 Güne başlama/bitirme KONUMU. Eskiden bu bilgi yalnız hareket akışındaki
              // day_open satırının GPS damgasındaydı; gün özetine bakan kullanıcı "nereden
              // başladı" sorusunu yanıtlayamıyordu.
              ['Başlangıç Konumu', fmtCoord(dayDetail?.startLatitude, dayDetail?.startLongitude)],
              ['Günü Bitirme', fmtTime(dayDetail?.endedAt)],
              ['Bitiş Konumu', fmtCoord(dayDetail?.endLatitude, dayDetail?.endLongitude)],
              ['Çalışma Süresi', fmtMinutes(dayDetail?.totalWorkMin)],
              ['Ziyaret Süresi', fmtMinutes(dayDetail?.totalVisitMin)],
              ['Yol Süresi', fmtMinutes(dayDetail?.totalTravelMin)],
              ['Toplam KM', fmtMoney(dayDetail?.totalKm, 0)],
              ['Zamana Uymama', fmtNum(dayDetail?.timeViolationCount)],
            ]} />
        </div>
      </div>
    </div>
  );
}

function ActivityGroup({ group: g, open, onToggle, onOpenDocument }) {
  const isDayRow = !g.visitId;

  return (
    <div className={`vgroup ${open ? 'open' : ''} ${isDayRow ? 'dayrow' : ''}`}>
      <div className="vghead" onClick={isDayRow ? undefined : onToggle}
        role={isDayRow ? undefined : 'button'} tabIndex={isDayRow ? undefined : 0}
        onKeyDown={(e) => !isDayRow && e.key === 'Enter' && onToggle()}>
        {!isDayRow && <span className="caret"><IcChevron size={14} /></span>}
        <span className="time">
          {fmtTime(g.startedAt)}{g.endedAt ? ` – ${fmtTime(g.endedAt)}` : ''}
        </span>
        {g.durationSec != null && <span className="dur">{fmtDuration(g.durationSec)}</span>}
        <span className="who">
          {g.customerCode && <small>{g.customerCode}</small>}
          {g.customerTitle || (isDayRow ? g.items[0]?.name : 'Müşteri bilinmiyor')}
        </span>
        {!isDayRow && g.isInRoute === false && <span className="pill soft a">Rut Dışı</span>}
        {g.outcome && (
          <span className={`pill soft ${OUTCOME_PILL[g.outcome] || 'gr'}`}>
            {OUTCOME_LABELS[g.outcome] || g.outcome}
          </span>
        )}
        {g.amount > 0 && <span className="amt">{fmtMoney(g.amount)} ₺</span>}
      </div>

      {(open || isDayRow) && (
        <div className="vgbody">
          {g.items.map((a) => (
            <ActivityRow key={a.id} a={a} onOpenDocument={onOpenDocument} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ a, onOpenDocument }) {
  // #777.4/#777.5 Link'i "belge no var mı"ya değil "gidilebilir kayıt var mı"ya bağla:
  // tahsilatlar documentType taşımaz (tıklanamıyordu), mobil siparişlerde ise belge no boş
  // gelebiliyor (link hiç çizilmiyordu). İkisi de artık aynı kuraldan geçer.
  const target = activityTarget(a);

  return (
    <div className={`arow ${a.isReversal ? 'rev' : ''}`}>
      <span className="at">{fmtTime(a.occurredAt, true)}</span>
      <span className={`dot a-${a.activityGroup || 'other'}`} title={a.name}>•</span>
      <span className="nm">{a.name || a.activityType}</span>
      <span className="meta">
        {target
          ? (
            <span className="docno" role="button" tabIndex={0}
              title="Kaydı aç"
              onClick={() => onOpenDocument(a)}
              onKeyDown={(e) => e.key === 'Enter' && onOpenDocument(a)}>
              {activityTargetLabel(a)}
            </span>
          )
          : a.documentNo && <span className="docno" style={{ cursor: 'default' }}>{a.documentNo}</span>}
        {a.skuCount != null && a.skuCount > 0 && <> · {a.skuCount} çeşit</>}
        {a.lineCount != null && a.lineCount > 0 && <> · {a.lineCount} kalem</>}
        {a.paymentType && <> · {PAYMENT_LABELS[a.paymentType] || a.paymentType}</>}
        {a.surveyQuestionCount != null && a.surveyQuestionCount > 0 && <> · {a.surveyQuestionCount} soru</>}
        {a.durationSec != null && a.durationSec > 0 && <> · {fmtDuration(a.durationSec)}</>}
        {a.reasonNote && <> · {a.reasonNote}</>}
        {a.latitude != null && <> · GPS ✓{a.gpsAccuracy != null ? ` ${Math.round(a.gpsAccuracy)} m` : ''}</>}
        {a.isOfflineCreated && <> · offline</>}
      </span>
      {a.amount != null && a.amount !== 0 && (
        <span className="amt">{fmtMoney(a.amount)} ₺</span>
      )}
    </div>
  );
}

/**
 * #788 Koordinat çifti → haritaya giden bağlantı. Konum alınamamışsa (GPS kapalı/sinyalsiz)
 * "—" değil AÇIK bir ifade yazılır: "—" okuyucuya "veri yok mu, alan mı yok" belirsizliği verir.
 */
function fmtCoord(lat, lng) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return 'konum yok';
  return (
    <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer">
      {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
    </a>
  );
}

function SummaryCard({ title, badge, rows }) {
  return (
    <div className="card">
      <div className="sec-h" style={{ padding: '11px 14px 8px', marginBottom: 0 }}>
        <div><b style={{ fontSize: 12.5 }}>{title}</b></div>
        <div style={{ flex: 1 }} />
        {badge && <span className="gpill">{badge}</span>}
      </div>
      {rows.map(([k, v]) => (
        <div className="srow" key={k}>
          <span className="k">{k}</span>
          <span className="v">{v}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Ardışık hareketleri ziyarete göre gruplar.
 *
 * Aynı ziyaretin hareketleri akışta ardışıktır (sunucu occurredAt'e göre sıralar), ama
 * gruplama anahtarı olarak visitId'yi ARDIŞIKLIKLA birlikte kullanıyoruz: aynı müşteriye
 * gün içinde ikinci kez girilirse (ayrı Visit satırı) iki ayrı grup çıkar — rapor "ziyaret
 * sayısı"nı doğru veren davranış budur (§10/1).
 */
function buildGroups(items) {
  const out = [];
  let cur = null;

  items.forEach((a, i) => {
    const vid = a.visitId || null;
    if (!cur || cur.visitId !== vid || !vid) {
      cur = {
        key: vid ? `v:${vid}` : `a:${a.id}`,
        visitId: vid,
        customerCode: a.customerCode,
        customerTitle: a.customerTitle,
        isInRoute: a.isInRoute,
        startedAt: a.occurredAt,
        endedAt: null,
        durationSec: null,
        amount: 0,
        outcome: null,
        items: [],
      };
      out.push(cur);
    }
    cur.items.push(a);
    cur.endedAt = a.occurredAt;
    if (a.amount) cur.amount += Number(a.amount);
    if (a.activityType === 'visit_end' || a.activityType === 'visit_start') {
      cur.customerTitle = cur.customerTitle || a.customerTitle;
    }
    // Grup sonucunu hareketlerden türet: belge/tahsilat varsa başarılı, no_sale varsa satış yok.
    if (a.documentType || a.paymentType) cur.outcome = 'success';
    else if (a.activityType === 'no_sale' && cur.outcome !== 'success') cur.outcome = 'no_sale';
    else if (a.activityType === 'visit_cancel') cur.outcome = 'cancelled';

    if (i === items.length - 1 && cur.visitId) {
      // no-op: süre aşağıda hesaplanır
    }
  });

  out.forEach((g) => {
    if (!g.visitId || !g.startedAt || !g.endedAt) return;
    const sec = Math.round((new Date(g.endedAt) - new Date(g.startedAt)) / 1000);
    g.durationSec = sec >= 0 ? sec : null;
  });

  return out;
}
